/**
 * Compatibility shim: react-router v5 surface on top of react-router-dom v7.
 *
 * Re-exports the v7 API and adds the v5 names the ported tree imports
 * (`useHistory`, `useRouteMatch`, `Switch`, `Redirect`, `Prompt`). The `Switch`
 * wrapper rewrites legacy `<Route component|render exact>` children into v7
 * `<Route element>` form at runtime, so route trees keep working without edits.
 *
 * Routing is the highest-risk part of the migration — verify navigation flows
 * and tighten any approximations (notably the non-exact `/*` heuristic and the
 * no-op `Prompt`) as they surface.
 */
import * as React from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
  matchPath,
} from 'react-router-dom';

export * from 'react-router-dom';
export { Routes, Route, Navigate };

/**
 * v5 useHistory -> v7 useNavigate adapter.
 *
 * The returned object MUST be referentially stable: v5's history object never
 * changed identity, and lots of code lists it in effect dependency arrays. If we
 * rebuilt it on every navigation, those effects would re-run on each route change
 * (e.g. authContext's redirect effect would push you back to its landing path).
 * `navigate` is stable across renders, so we memo on it alone and read the live
 * location through a ref-backed getter.
 */
export function useHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  // Read navigate/location through refs so the history object can be created
  // exactly once ([] deps) and keep a stable identity for the component's life —
  // matching v5. (RR7's useNavigate identity is not guaranteed stable across
  // navigations, so we must not depend on it.)
  const navigateRef = React.useRef(navigate);
  navigateRef.current = navigate;
  const locationRef = React.useRef(location);
  locationRef.current = location;
  return React.useMemo(
    () => ({
      push: (to: any, state?: any) => navigateRef.current(to, { state }),
      replace: (to: any, state?: any) => navigateRef.current(to, { replace: true, state }),
      go: (n: number) => navigateRef.current(n),
      goBack: () => navigateRef.current(-1),
      goForward: () => navigateRef.current(1),
      get location() {
        return locationRef.current;
      },
      get length() {
        return window.history.length;
      },
      action: 'POP' as const,
      listen: () => () => {},
      block: () => () => {},
      createHref: (loc: any) => (typeof loc === 'string' ? loc : loc?.pathname ?? ''),
    }),
    []
  );
}

/** v5 useRouteMatch -> v7 (uses matchPath / current location). */
export function useRouteMatch(pattern?: any): any {
  const location = useLocation();
  const params = useParams();
  if (pattern == null) {
    // v5's `match.path` is the route PATTERN (`/knowledgehub/:sectionName/...`),
    // not the resolved URL. RR7 hooks only expose the resolved pathname, so
    // reconstruct the pattern by swapping any segment that equals a param value
    // back to its `:paramName` placeholder (callers like the KnowledgeHub
    // breadcrumbs rely on this to label each crumb).
    const valueToKey: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      // Skip the splat ('*') param: its value is a literal path tail, not a named
      // placeholder. Mapping it back corrupts `match.path` into `.../:*` and breaks
      // links built from it (e.g. `${match.path}/view/:id`) — modules like SafetyNest
      // are mounted under a `/safetynest/*` splat, so this hit every list page.
      if (key === '*') continue;
      if (value != null) valueToKey[String(value)] = key;
    }
    const patternPath =
      '/' +
      location.pathname
        .split('/')
        .filter(Boolean)
        .map((seg) => {
          let decoded = seg;
          try {
            decoded = decodeURIComponent(seg);
          } catch {
            /* keep raw */
          }
          const key = valueToKey[seg] ?? valueToKey[decoded];
          return key ? `:${key}` : seg;
        })
        .join('/');
    return { path: patternPath, url: location.pathname, params, isExact: true };
  }
  const opts = typeof pattern === 'string' ? { path: pattern } : pattern;
  const path = opts.path ?? opts;
  const matched = matchPath({ path, end: opts.exact ?? false }, location.pathname);
  if (!matched) return null;
  return {
    path,
    url: matched.pathnameBase ?? matched.pathname,
    params: matched.params,
    isExact: matched.pathname === location.pathname,
  };
}

/** v5 <Redirect to push from> -> v7 <Navigate>. */
export function Redirect({ to, push }: { to: any; push?: boolean; from?: string }) {
  return <Navigate to={to} replace={!push} />;
}

/** v5 <Prompt> is unsupported under v7's data routers; no-op for now. */
export function Prompt(_props: { when?: boolean; message?: any }) {
  return null;
}

function transformChild(child: React.ReactNode, basePath: string): React.ReactNode {
  if (!React.isValidElement(child)) return child;
  const props: any = child.props;

  // A descendant <Routes> matches RELATIVE to its parent route. The ported tree
  // authors nested route paths as ABSOLUTE (e.g. SafetyNest's `/safetynest/audit`
  // mounted under the `/safetynest/*` route), which never match relatively. When
  // this Switch is nested (basePath !== "/"), strip that base prefix so absolute
  // child paths become the relative paths v7 expects. Top-level Switches
  // (basePath === "/") are left untouched.
  const relativise = (p?: string): string | undefined => {
    if (!p || basePath === '/' || basePath === '') return p;
    if (p === basePath) return '';
    if (p.startsWith(basePath + '/')) return p.slice(basePath.length + 1);
    return p;
  };

  // <Redirect> inside a <Switch>
  if (child.type === Redirect) {
    const path = relativise(props.from) ?? '*';
    return <Route path={path} element={<Navigate to={props.to} replace={!props.push} />} />;
  }

  const looksLikeRoute =
    child.type === Route || 'path' in props || 'exact' in props || 'component' in props || 'render' in props;
  if (!looksLikeRoute) return child;

  const { component: Comp, render, children: routeChildren, path, exact, element } = props;

  let el = element as React.ReactNode;
  if (el == null) {
    if (Comp) el = React.createElement(Comp);
    else if (typeof render === 'function') el = render(props);
    else if (routeChildren != null) el = routeChildren;
  }

  // v5 routes were non-exact by default; v7 routes are exact. Append `/*` so
  // parent routes that mount nested switches keep matching deeper paths.
  let p: string | undefined = relativise(path);
  if (p && p !== '*' && !exact) {
    p = p.replace(/\/+$/, '') + '/*';
  }

  return <Route path={p} element={el} />;
}

/** v5 <Switch> -> v7 <Routes>, converting legacy Route children. */
export function Switch({ children }: { children?: React.ReactNode }) {
  // Determine the parent route's base pathname so absolute child paths can be
  // relativised for nested switches.
  //  - Top level (not inside a Route): no `*` param -> base "/".
  //  - Nested under a splat route (e.g. "/safetynest/*"): the base is the current
  //    location MINUS the splat tail. We must NOT use useResolvedPath('.') here —
  //    for a splat route it resolves to the FULL location (e.g. "/safetynest/audit"),
  //    which would over-strip and break every sub-route.
  const location = useLocation();
  const params = useParams();
  const splat = (params as Record<string, string | undefined>)['*'];
  let basePath = '/';
  if (splat !== undefined) {
    const cut = location.pathname.length - splat.length;
    basePath = location.pathname.slice(0, cut).replace(/\/+$/, '') || '/';
  }
  return <Routes>{React.Children.map(children, (c) => transformChild(c, basePath))}</Routes>;
}
